(ns ncr.symbolic
  (:require
   [taoensso.encore :as e]
   [ncr.common.util :as u]
   [clojure.core.match :as match]))

(def ^:private _TYPES
  {:role/segment [:or :keyword :string]

   :expr
   [:map
    [:etype [:enum :atom :form :stack]]
    [:path [:vector :role/segment]]
    [:role :role/segment]
    [:context [:map-of [:or :string :keyword] [:set :any]]]
    [:value :any]

    [:op :keyword] ; (not= etype :atom)
    [:parts [:vector :expr]] ; (= etype :stack)
    [:roles [:map-of :any [:tuple :role/segment :expr]] ; (= etype :form)
     [:order [:vector :any]]]] ; (= etype :form)
   ;;
   })

(defn- ensure-path [p]
  (cond-> p (not (vector? p)) vector))

(defn make-atom [path value & [context]]
  (let [path (ensure-path path)]
    {:etype :atom
     :value value
     :role (peek path)
     :path path
     :context (or context {})}))

(def ^:private _tree1
  [[:+ :income]
   [:- :expense
    [:+ :direct]
    [:+ :team
     [:+ ":team"
      [:* :coefficient {:etype :atom :value 1.00M}]
      [:* :time/task]
      [:/ :time/total]
      [:* :invoice]]]]])

(defn fix-path
  ([v] (fix-path v []))
  ([{:keys [etype role] :as v} path]
   (let [v (assoc v :path (conj path role))]
     (case etype
       :stack
       (assoc v :parts
         (vec (map-indexed #(fix-path %2 (conj path %1)) (:parts v))))

       :form
       (u/reduce-for-as-> v $
         [role1 (:order v)]
         (update-in $ [:roles role1 1] fix-path (:path v)))

       v))))

(declare create)

(defmulti create*
  (fn [{:keys [etype]} args] etype))

#_(ns-unmap *ns* 'create*)

(defmethod create* :default [& args]
  (throw (ex-info "Unknown etype in create*" {:args args})))

(defmethod create* :stack [opts _]
  (-> opts
    (select-keys [:path :etype :op])
    (assoc :parts [] :role (peek (:path opts)))))

(defmethod create* :atom [opts _]
  (-> opts
    (select-keys [:path :etype :value])
    (assoc :role (peek (:path opts)))))

(defmethod create* :expr [{:keys [expr] :as opts} _]
  (let [e (e/have! expr)]
    (merge
      (select-keys opts [:path])
      (if (fn? expr)
        (expr)
        expr))))

(defn add-role [{:keys [etype path roles] :as v} op role1 spec]
  (assert (= :form etype))
  (e/have! nil? (find roles role1))
  (-> v
    (update :order conj role1)
    (update :roles assoc role1
      [op (create (conj path role1) spec)])))

(defmethod create* :form [{:keys [path] :as opts} args]
  (-> opts
    (select-keys [:path :etype])
    (assoc :order [] :roles {} :role (peek path))
    (u/reduce-for-as-> $
      [[op role1 :as one] args
       :let [spec (subvec one 2)]]
      (if (keyword? role1)
        (add-role $ op role1 spec)
        (do
          (e/have! nil? (:template $))
          (assoc $ :template one))))))

(defn create
  ([path] (create path [:+]))
  ([path spec]
   (let [path (ensure-path path)]
     (match/match [spec]
       [[(opts :guard map?) & r]]
       (create* (merge {:path path} opts) r)

       [([] :seq)]
       (recur path [:+])

       [[(op :guard keyword?)]]
       (recur path [{:etype :stack :op op}])

       [[[(op :guard keyword?) role1 & _] & _]]
       (recur path (into [{:etype :form}] spec))
       ;;
       ))))

#_(create :aaa [:+])
#_(create :aaa [{:etype :atom :value 1.00M}])
#_(create :bbb [{:etype :expr :expr (create :aaa [:+])}])
#_(fix-path (create :pnl _tree1))

(defmulti collect
  (fn [{:keys [etype] :as v} path w] etype))

#_(ns-unmap *ns* 'create*)

(defmethod collect :default [& args]
  (throw (ex-info "Unknown etype in collect" {:args args})))

(defmethod collect :stack [{:keys [role] :as v} path w]
  (e/have! empty? path)
  (-> v
    (dissoc :value)
    (update :parts conj w)))

(defn- ensure-role [{:keys [roles template] :as form} role]
  (if (contains? roles role)
    form
    (let [[op] (e/have! template :data {:is (keys roles) :ought role})]
      (add-role form op role (subvec template 2)))))

(defmethod collect :form [{:keys [roles] :as v} [role :as path] w]
  (-> v
    (dissoc :value)
    (ensure-role role)
    (update-in [:roles role 1] collect (subvec path 1) w)))

#_(u/reduce-for-as-> (create :aaa [:+]) $
    [piece (range 6)]
    (collect $ [] (make-atom [:expense] piece)))

#_(u/reduce-for-as-> (create :aaa [[:+ :income] [:- :expense]]) $
    [[role v]
     [[:income 1]
      [:expense 2]
      [:income 3]
      [:expense 4]
      [:income 5]]]
    (collect $ [role] (make-atom [role] v)))

(def ignore-nils
  (filter some?))

#_(reduce (ignore-nils +) [1 2 nil 3 4])

(defn promote-nils [f]
  #(if (every? some? %&)
     (apply f %&)
     (reduced nil)))

#_(reduce (promote-nils #(do (prn %&) (apply * %&))) [1 2 3 4])
#_(reduce (promote-nils #(do (prn %&) (apply * %&))) [1 2 nil 3 4])

(defn with-identity [f id]
  (fn
    ([] id)
    ([& args] (apply f args))))

#_((-> / promote-nils (with-identity 1)))
#_((-> / promote-nils (with-identity 1)) 2 nil)

#?(:clj
   (defn- bigdec-div [a b]
     (if (some #(instance? BigDecimal %) [a b])
       (bigdec (/ (double a) b))
       (/ a b))))

(def default-ops
  {:+ (ignore-nils +)
   :- (ignore-nils (with-identity - 0))
   :* (promote-nils *)
   :/ (promote-nils (with-identity #?(:clj bigdec-div :cljs /) 1))
   :conj (with-identity conj [])})

(def defaults
  {:ops default-ops
   :groups {:+ :-
            :* :/}
   :context-fn #(merge-with into %1 %2)})

(defmulti recalc*
  (fn [{:keys [etype]} _] etype))

(defn recalc [v & [opts]]
  (recalc* v (merge defaults opts)))

(defmethod recalc* :default [v _]
  (e/have! (contains? v :value))
  v)

#_(transduce (map :v) (default-ops :*) [{:v 2} {:v 3} {:v 4}])
#_(transduce (map :v) (default-ops :*) [{:v 2} nil {:v 3} {:v 4}])
#_(transduce (map :v) (default-ops :*) [{:v 2} nil {:v :a} {:v "q"}])

(defmethod recalc* :stack [{:keys [op parts] :as v} {:keys [ops] :as opts}]
  (if-not (seq parts)
    (assoc v :value nil)
    (let [parts! (transient parts)
          rv
          (transduce
            (map-indexed
              #(if-let [[_ v] (find %2 :value)]
                 v
                 (let [part (recalc* %2 opts)]
                   (assoc! parts! %1 part)
                   (:value part))))
            (e/have! (ops op))
            parts)
          parts (persistent! parts!)]
      (assoc v
        :context (->> parts (map :context) (reduce (:context-fn opts) {}))
        :parts parts
        :value (unreduced rv)))))

#_(recalc (create :aaa [:+]))

#_(-> (create :aaa [:+])
    (u/reduce-for-as-> $
      [piece (range 6)]
      (collect $ [] (make-atom [:expense] piece)))
    recalc)

(defmethod recalc* :form [{:keys [roles order] :as v} {:keys [ops] :as opts}]
  (let [id-fn (-> order first roles first ops)
        roles! (transient roles)
        rv
        (reduce
          #(let [[op r] (roles %2)
                 vr
                 (if-let [[_ vr] (find r :value)]
                   vr
                   (let [r (recalc* r opts)]
                     (assoc! roles! %2 [op r])
                     (:value r)))]
             ((e/have! (ops op)) (or %1 ((e/have! id-fn))) vr))
          nil
          order)
        roles (persistent! roles!)
        rvals (map peek (vals roles))
        rv (when (some :value rvals) rv)]
    (assoc v
      :context (reduce (:context-fn opts) {} (map :context rvals))
      :roles roles
      :value rv)))

#_(-> (create :aaa [[:+ :income] [:- :expense]])
    (u/reduce-for-as-> $
      [[role v]
       [[:income 1]
        [:expense 2]
        [:income 3]
        [:expense 4]
        [:income 5]]]
      (collect $ [role] (make-atom [role] v)))
    recalc)

#_(u/reduce-for-as-> (create :aaa _tree1) $
    [[role v]
     [[[:income] 1]
      [[:income] 3]
      [[:expense :direct] 10]
      [[:expense :team "aaa" :time/task] 10]
      [[:expense :team "aaa" :time/total] 20]
      [[:expense :team "aaa" :invoice] 21]
      [[:expense :team "aaa" :invoice] 25]
      [[:income] 5]]]
    (collect $ role (make-atom role v)))

(defn- xlate-op [op {:keys [groups ops] :as opts}]
  (if-let [me (find groups op)]
    (set me)
    (if-let [me (find (e/invert-map groups) op)]
      (into {} [me ((juxt val key) me)])
      (do
        (e/have! ops op)
        #{op}))))

#_(xlate-op :+ defaults)
#_(xlate-op :- defaults)

(defmulti fuse*
  (fn [l op r opts] [(:etype l) (:etype r)]))

#_(ns-unmap *ns* 'fuse*)

(defmethod fuse* :default [& args]
  (throw (ex-info "Unknown etype in fuse*" {:args args})))

(defmethod fuse* [:stack :stack] [{l-op :op :as l} op {r-op :op :as r} opts]
  (let [xop (e/have! (xlate-op op opts))]
    (e/have! #(= l-op %) (xop r-op))
    (-> l
      (dissoc :value)
      (update :parts into (:parts r)))))

(defmethod fuse* [:form :form] [l op r opts]
  (let [xop (e/have! (xlate-op op opts))]
    (u/reduce-for-as-> (dissoc l :value) $
      [[r-role [r-op r-sub]] (:roles r)]
      (if-let [[l-op l-sub] (get-in l [:roles r-role])]
        (let [op2 (e/have! (xop r-op))
              xop2 (xlate-op l-op opts)]
          (e/have! xop l-op)
          (update-in $ [:roles r-role 1] fuse* (xop2 op2) r-sub opts))
        (-> $
          (update :order conj r-role)
          (assoc-in [:roles r-role] [(xop r-op) r-sub]))))))

(defn fuse [l op r & [opts]]
  (fix-path (fuse* l op r (merge defaults opts))))

(defn fuse-tuple [{:keys [path] :as l} op r & [opts]]
  (assert (= (:etype l) (:etype r) :form))
  (assert (= (:order l) (:order r)))

  (let [spec
        (mapv
          #(let [[l-op l-sub] (get-in l [:roles %])
                 [r-op r-sub] (get-in r [:roles %])]
             (assert (= :conj l-op r-op))
             [:conj % {:etype :expr :expr (fuse l-sub op r-sub opts)}])
          (:order l))]
    (fix-path (create path spec))))

(defn get-path [{:keys [etype] :as v} path]
  (if-not (seq path)
    v
    (let [[head & tail] path]
      (case etype
        :stack
        (recur (e/have! (get-in v [:parts (e/have! int? head)])) tail)

        :form
        (recur (e/have! (get-in v [:roles head 1])) tail)))))

(defn update-path
  ([v path f arg & args] (update-path v path #(apply f % arg args)))
  ([{:keys [etype] :as v} path f]
   (e/have! v)
   (if-not (seq path)
     (f v)
     (let [v (dissoc v :value)
           [head & tail] path
           k
           (case etype
             :stack
             [:parts (e/have! int? head)]

             :form
             [:roles head 1])]
       (update-in v k update-path tail f)))))

#_(-> (create :aaa [[:+ :income] [:- :expense]])
    (u/reduce-for-as-> $
      [[role v]
       [[:income 1]
        [:expense 2]
        [:income 3]
        [:expense 4]
        [:income 5]]]
      (collect $ [role] (make-atom [role] v)))
    recalc
    (get-path [:income 1]))
